/**
 * normalizeCustomerType — JHA short-code → canonical long-form mapping.
 *
 * Why: JHA Core Director's `CustType` field returns single-character codes
 * ("I" for Individual, "B" for Business, etc.). The v3 CustomerType enum +
 * every product `customerTypes` whitelist + every disclosure
 * `applicableEntityTypes` filter is keyed on the LONG-FORM canonical names
 * ('Individual' / 'JointTenant' / 'Corporation' / etc.). Persisting the raw
 * short-code into `cart.peopleJson.customerType` causes silent failure modes
 * downstream — Bill flagged 2026-05-08 demo: every product tile filtered out
 * because `cart.peopleJson[i].customerType === 'I'` didn't match any
 * long-form whitelist entry.
 *
 * Two parser seams call this:
 *   - `personFromRow` in components/people/PersonSearchBar.tsx (CustomerSearchRow → RaoPerson)
 *   - `useInquiryHydration` in components/personDialog/shared/useInquiryHydration.ts (parseCustInq.customerType → RaoPerson)
 *
 * Long-form values pass through unchanged (idempotent). Unknown short codes
 * default to 'Individual' — the call-class gate (callClassFor + product
 * `allowedCallClasses`) handles cohort routing regardless of the long-form
 * label, so this fallback is safe; the cohort-specific dialog will adjust
 * the customerType to the precise sub-type before save anyway.
 *
 * See memory: feedback_people_modal_persists_short_customer_type_20260508.
 */
import type { CustomerType } from '@retail-ops/shared-types/enums';

const SHORT_TO_LONG: Record<string, CustomerType> = {
  // JHA CustType single-char codes observed in PCB's Core Director.
  // The 'Business' value is JHA's catch-all for the business cohort —
  // the cohort-picker / business sub-type dialog refines it to LLC /
  // Corporation / Partnership / DBA after CIF intake.
  I: 'Individual',
  B: 'Corporation',
  Business: 'Corporation',
  // Defensive coverage of other observed short codes; cohort routing via
  // call-class gate handles eligibility regardless of label.
  M: 'Minor',
  T: 'RevocableTrust',
  E: 'Estate',
};

/**
 * Map a raw `customerType` (short code or long form) to the canonical
 * long-form value. Idempotent for long-form input. Returns 'Individual'
 * for empty/unknown input — safe default; cohort dialog refines.
 */
export function normalizeCustomerType(raw: string | null | undefined): CustomerType {
  if (!raw) return 'Individual';
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return 'Individual';

  // Short codes (length <= 2): consult lookup, else default.
  if (trimmed.length <= 2) {
    const mapped = SHORT_TO_LONG[trimmed];
    if (mapped) return mapped;
    return 'Individual';
  }

  // Long form: pass through if it's a known canonical value, else default.
  // The runtime cast is safe — downstream consumers either match against
  // the canonical enum (in which case unknown values fall through to the
  // call-class gate) or treat it as a free-string label.
  if (trimmed === 'Business') return 'Corporation';
  return trimmed as CustomerType;
}

/**
 * Predicate: true when `raw` is already a long-form canonical-looking value
 * (length > 2). Useful for skipping normalization when the data is already
 * clean (e.g. when reading from cart.peopleJson written by a recent v3 modal).
 */
export function isLongFormCustomerType(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const trimmed = String(raw).trim();
  return trimmed.length > 2 && trimmed !== 'Business';
}
